import { supabase } from '../lib/supabase';

export class LogoStorageService {
  static BUCKET_NAME = 'qr-logos';

  /**
   * Upload a logo file to Supabase Storage
   */
  static async uploadLogo(file, userId) {
    try {
      if (!file || !userId) {
        throw new Error('File and userId are required');
      }

      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
      if (!allowedTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Please upload PNG, JPG, WEBP, or SVG files.');
      }

      // Validate file size (5MB max)
      if (file?.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Generate unique filename
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}_${Math.random()?.toString(36)?.substring(2)}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      // Upload file to Supabase Storage
      const { data, error } = await supabase?.storage?.from(this.BUCKET_NAME)?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL for the uploaded file
      const { data: urlData } = supabase?.storage?.from(this.BUCKET_NAME)?.getPublicUrl(filePath);

      return {
        success: true,
        filePath: data?.path,
        publicUrl: urlData?.publicUrl,
        fileName: file?.name,
        fileSize: file?.size
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message
      };
    }
  }

  /**
   * Delete a logo file from storage
   */
  static async deleteLogo(filePath) {
    try {
      const { error } = await supabase?.storage?.from(this.BUCKET_NAME)?.remove([filePath]);

      if (error) {
        throw new Error(`Delete failed: ${error.message}`);
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error?.message
      };
    }
  }

  /**
   * Get user's uploaded logos
   */
  static async getUserLogos(userId) {
    try {
      const { data: files, error } = await supabase?.storage?.from(this.BUCKET_NAME)?.list(`${userId}/`, {
          limit: 50,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        throw new Error(`Failed to load logos: ${error.message}`);
      }

      // Generate public URLs for each logo
      const logosWithUrls = files?.map(file => {
        const filePath = `${userId}/${file?.name}`;
        const { data: urlData } = supabase?.storage?.from(this.BUCKET_NAME)?.getPublicUrl(filePath);

        return {
          name: file?.name,
          filePath,
          publicUrl: urlData?.publicUrl,
          size: file?.metadata?.size,
          createdAt: file?.created_at
        };
      });

      return {
        success: true,
        logos: logosWithUrls || []
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message,
        logos: []
      };
    }
  }

  /**
   * Validate and optimize image for QR code usage
   */
  static async optimizeImageForQR(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for optimization
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate optimal size (max 400px to prevent memory issues)
          const maxSize = 400;
          const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
          const newWidth = Math.round(img.width * scale);
          const newHeight = Math.round(img.height * scale);
          
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          // Draw optimized image
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          
          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              resolve({
                success: true,
                optimizedFile: new File([blob], file.name, {
                  type: 'image/png',
                  lastModified: Date.now()
                }),
                originalSize: file.size,
                optimizedSize: blob.size,
                dimensions: { width: newWidth, height: newHeight }
              });
            },
            'image/png',
            0.8 // Compression quality
          );
        };
        img.onerror = () => {
          resolve({
            success: false,
            error: 'Invalid image file'
          });
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        resolve({
          success: false,
          error: 'Failed to read file'
        });
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Enhanced image processing with network error handling
   */
  static async processImageForQR(imageUrl, maxRetries = 2) {
    let lastError;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Create blob URL with CORS handling
        const blobResult = await this.createBlobUrl(imageUrl);
        
        if (!blobResult?.success) {
          lastError = new Error(blobResult?.error);
          continue;
        }

        // Process the image
        const result = await this.processImageFromBlob(blobResult?.blobUrl);
        
        // Clean up blob URL
        blobResult?.cleanup?.();
        
        return result;
      } catch (error) {
        console.log(`Image processing attempt ${attempt + 1} failed:`, error);
        lastError = error;
        
        // Wait before retry
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }

    throw lastError || new Error('Image processing failed after all retries');
  }

  /**
   * Process image from blob URL
   */
  static async processImageFromBlob(blobUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          // Create canvas for processing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate optimal size (max 400px to prevent memory issues)
          const maxSize = 400;
          const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
          const newWidth = Math.round(img.width * scale);
          const newHeight = Math.round(img.height * scale);
          
          canvas.width = newWidth;
          canvas.height = newHeight;
          
          // Draw optimized image
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          
          // Convert to data URL
          const dataUrl = canvas.toDataURL('image/png', 0.9);
          
          resolve({
            success: true,
            dataUrl,
            dimensions: { width: newWidth, height: newHeight },
            processed: true
          });
        } catch (canvasError) {
          reject(new Error(`Image processing failed: ${canvasError?.message}`));
        }
      };

      img.onerror = (error) => {
        reject(new Error('Failed to load image for processing'));
      };

      // Set crossOrigin to handle CORS
      img.crossOrigin = 'anonymous';
      img.src = blobUrl;
      
      // Timeout for image loading
      setTimeout(() => {
        reject(new Error('Image loading timeout'));
      }, 10000);
    });
  }

  /**
   * Enhanced network error handling with better fallback strategies
   */
  static async createBlobUrl(imageUrl) {
    try {
      let response;
      let attempts = [
        // Direct fetch with enhanced timeout handling
        async () => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller?.abort(), 5000); // Reduced timeout
          try {
            const res = await fetch(imageUrl, {
              signal: controller?.signal,
              mode: 'cors',
              credentials: 'omit',
              cache: 'force-cache',
              headers: {
                'Accept': 'image/*',
              }
            });
            clearTimeout(timeoutId);
            return res;
          } catch (error) {
            clearTimeout(timeoutId);
            throw error;
          }
        },
        // Primary proxy with better error handling and cookie management
        async () => {
          const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(imageUrl)}&w=400&h=400&fit=inside&we&output=png&errorredirect=404`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller?.abort(), 8000);
          try {
            const res = await fetch(proxyUrl, {
              signal: controller?.signal,
              mode: 'cors',
              credentials: 'omit',
              cache: 'no-cache',
              headers: {
                'Accept': 'image/*',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            });
            clearTimeout(timeoutId);
            return res;
          } catch (error) {
            clearTimeout(timeoutId);
            throw error;
          }
        },
        // Secondary proxy fallback with cookie management
        async () => {
          const proxyUrl = `https://wsrv.nl/?url=${encodeURIComponent(imageUrl)}&w=400&h=400&fit=inside&output=png`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller?.abort(), 8000);
          try {
            const res = await fetch(proxyUrl, {
              signal: controller?.signal,
              mode: 'cors',
              credentials: 'omit',
              cache: 'no-cache',
              headers: {
                'Accept': 'image/*',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            });
            clearTimeout(timeoutId);
            return res;
          } catch (error) {
            clearTimeout(timeoutId);
            throw error;
          }
        },
        // Last resort: try to load as base64
        async () => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            img.onload = () => {
              canvas.width = Math.min(img.width, 400);
              canvas.height = Math.min(img.height, 400);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              canvas.toBlob(blob => {
                if (blob) {
                  resolve({ ok: true, blob: () => Promise.resolve(blob) });
                } else {
                  reject(new Error('Canvas to blob conversion failed'));
                }
              }, 'image/png', 0.8);
            };
            
            img.onerror = () => reject(new Error('Image load failed'));
            img.src = imageUrl;
            
            // Timeout for image loading
            setTimeout(() => reject(new Error('Image load timeout')), 5000);
          });
        }
      ];

      // Try each approach with improved error tracking
      let lastError;
      for (let i = 0; i < attempts?.length; i++) {
        try {
          response = await attempts?.[i]();
          if (response?.ok) {
            break;
          } else {
            lastError = new Error(`HTTP ${response?.status}: ${response?.statusText}`);
          }
        } catch (attemptError) {
          lastError = attemptError;
          console.log(`Logo fetch attempt ${i + 1} failed:`, attemptError?.message);
          
          // Add progressive delay between attempts
          if (i < attempts?.length - 1) {
            await new Promise(resolve => setTimeout(resolve, Math.min(500 * (i + 1), 2000)));
          }
        }
      }

      if (!response?.ok) {
        throw lastError || new Error('All image loading attempts failed');
      }

      const blob = await response?.blob();
      
      // Validate blob
      if (!blob || blob?.size === 0) {
        throw new Error('Received empty image data');
      }
      
      const blobUrl = URL.createObjectURL(blob);
      
      return {
        success: true,
        blobUrl,
        cleanup: () => URL.revokeObjectURL(blobUrl)
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to load logo image: ${error?.message}. Try uploading the logo directly or check the image URL.`
      };
    }
  }

  /**
   * Safe image validation with network error recovery
   */
  static async validateLogoUrl(url) {
    try {
      // Enhanced URL validation
      const urlObj = new URL(url);
      if (!['http:', 'https:']?.includes(urlObj?.protocol)) {
        throw new Error('Only HTTP and HTTPS URLs are supported');
      }

      // Check for common image extensions
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
      const hasImageExtension = imageExtensions?.some(ext => 
        urlObj?.pathname?.toLowerCase()?.includes(ext)
      );
      
      // If no image extension, try to validate by attempting to load
      if (!hasImageExtension) {
        console.warn('URL does not appear to be an image file');
      }

      // Try to create blob URL for validation
      const blobResult = await this.createBlobUrl(url);
      
      if (blobResult?.success) {
        // Clean up immediately after validation
        blobResult?.cleanup?.();
        return {
          success: true,
          url,
          contentType: 'image/png',
          useProxy: true,
          message: 'Image URL validated successfully'
        };
      } else {
        return {
          success: false,
          error: blobResult?.error || 'Image validation failed',
          suggestions: [
            'Check if the image URL is accessible',
            'Try uploading the image directly instead',
            'Ensure the image is in a supported format (PNG, JPG, WebP, SVG)'
          ]
        };
      }
    } catch (error) {
      return {
        success: false,
        error: `URL validation failed: ${error?.message}`,
        suggestions: [
          'Check if the URL is properly formatted',
          'Ensure the URL starts with http:// or https://',
          'Try a different image source'
        ]
      };
    }
  }

  /**
   * Create a proxy URL for external images to avoid CORS issues
   */
  static createProxyUrl(originalUrl) {
    try {
      // Use multiple proxy services for better reliability
      const proxies = [
        `https://images.weserv.nl/?url=${encodeURIComponent(originalUrl)}&w=400&h=400&fit=inside&we&output=png&errorredirect=404`,
        `https://wsrv.nl/?url=${encodeURIComponent(originalUrl)}&w=400&h=400&fit=inside&output=png`,
        // Fallback to original if proxies fail
        originalUrl
      ];
      
      // Return first proxy URL for now
      return proxies?.[0];
    } catch (error) {
      console.log('Proxy URL creation failed:', error);
      return originalUrl; // Fallback to original URL
    }
  }

  /**
   * Clear problematic cookies that might cause domain issues
   */
  static clearProblematicCookies() {
    try {
      // Clear Cloudflare cookies that might cause domain issues
      const problematicCookies = ['__cf_bm', '__cfduid', '_cfuvid'];
      const domain = window.location.hostname;
      
      problematicCookies.forEach(cookieName => {
        // Clear for current domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
        // Clear for parent domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${domain}`;
        // Clear for root domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });
    } catch (error) {
      console.log('Cookie clearing failed (non-critical):', error);
    }
  }

  /**
   * Load image with enhanced CORS and cookie handling
   */
  static async loadImageWithCORS(imageUrl, timeout = 10000) {
    return new Promise((resolve) => {
      try {
        // Clear problematic cookies first
        this.clearProblematicCookies();
        
        const img = new Image();
        let resolved = false;
        
        const cleanup = () => {
          if (!resolved) {
            resolved = true;
            img.onload = null;
            img.onerror = null;
          }
        };
        
        img.onload = () => {
          cleanup();
          resolve({
            success: true,
            image: img,
            width: img.width,
            height: img.height
          });
        };
        
        img.onerror = () => {
          cleanup();
          resolve({
            success: false,
            error: 'Image failed to load'
          });
        };
        
        // Set timeout
        const timeoutId = setTimeout(() => {
          if (!resolved) {
            cleanup();
            resolve({
              success: false,
              error: 'Image loading timeout'
            });
          }
        }, timeout);
        
        // Clear timeout on completion
        const originalOnLoad = img.onload;
        const originalOnError = img.onerror;
        
        img.onload = (e) => {
          clearTimeout(timeoutId);
          originalOnLoad?.(e);
        };
        
        img.onerror = (e) => {
          clearTimeout(timeoutId);
          originalOnError?.(e);
        };
        
        // Configure image for CORS and privacy
        img.crossOrigin = 'anonymous';
        img.referrerPolicy = 'no-referrer';
        img.src = imageUrl;
        
      } catch (error) {
        resolve({
          success: false,
          error: `Image loading failed: ${error?.message}`
        });
      }
    });
  }

  /**
   * Safe image preload with enhanced error handling for QR generation
   */
  static async preloadImageForQR(imageUrl, timeout = 10000) {
    return new Promise(async (resolve) => {
      try {
        // Clear problematic cookies before loading
        this.clearProblematicCookies();
        
        // First get a working blob URL
        const blobResult = await this.createBlobUrl(imageUrl);
        
        if (!blobResult?.success) {
          resolve({
            success: false,
            error: blobResult?.error || 'Failed to fetch image'
          });
          return;
        }

        const img = new Image();
        let resolved = false;

        const cleanup = () => {
          if (!resolved) {
            resolved = true;
            img.onload = null;
            img.onerror = null;
            blobResult?.cleanup?.();
          }
        };

        img.onload = () => {
          cleanup();
          resolve({
            success: true,
            image: img,
            dataUrl: blobResult?.blobUrl,
            width: img.width,
            height: img.height
          });
        };

        img.onerror = () => {
          cleanup();
          resolve({
            success: false,
            error: 'Image failed to load after processing'
          });
        };

        // Set timeout for image loading
        const timeoutId = setTimeout(() => {
          if (!resolved) {
            cleanup();
            resolve({
              success: false,
              error: 'Image loading timeout'
            });
          }
        }, timeout);

        // Clear timeout on completion
        const originalOnLoad = img.onload;
        const originalOnError = img.onerror;
        
        img.onload = (e) => {
          clearTimeout(timeoutId);
          originalOnLoad?.(e);
        };
        
        img.onerror = (e) => {
          clearTimeout(timeoutId);
          originalOnError?.(e);
        };

        // Set CORS and start loading with better error handling
        img.crossOrigin = 'anonymous';
        img.referrerPolicy = 'no-referrer';
        img.src = blobResult?.blobUrl;

      } catch (error) {
        resolve({
          success: false,
          error: `Preload failed: ${error?.message}`
        });
      }
    });
  }
}

export default LogoStorageService;